import * as THREE from 'three';
import type { WindField } from '../engine/wind';

// Ambient "the world is alive" effects, all bounded + instanced so they cost almost nothing:
//   - FallingLeaves: leaves that detach from nearby canopies and drift down on the wind (more during gusts).
//   - Birds: a RARE small flock that crosses the sky every few minutes (daytime), scattering when a shot goes off.
// Both read the shared WindField so they lean with the same gust the canopies do. Neither casts shadows and
// both are pooled (fixed instance counts), so there is no per-frame allocation and the draw cost is one call each.

const TMP_M = new THREE.Matrix4();
const TMP_Q = new THREE.Quaternion();
const TMP_Q2 = new THREE.Quaternion();
const TMP_S = new THREE.Vector3();
const TMP_S2 = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);
const FWD_X = new THREE.Vector3(1, 0, 0);
const ONE = new THREE.Vector3(1, 1, 1);
const LEAF_AXIS = new THREE.Vector3(0.5, 1, 0.3).normalize(); // fixed tilted tumble axis for falling leaves

// ---- Falling leaves ---------------------------------------------------------------------------------------
// gx/gz = a persistent per-leaf outward GLIDE (the wind-biased fling that carries a leaf 5..30m from the tree),
// decaying over its flight so it settles into the ambient drift near the end.
interface Leaf { x: number; y: number; z: number; vx: number; vy: number; vz: number; gx: number; gz: number; spin: number; ang: number; life: number; size: number; alive: boolean; }

export class FallingLeaves {
  readonly mesh: THREE.InstancedMesh;
  private readonly leaves: Leaf[] = [];
  private readonly col = new THREE.Color();
  private trickle = 0;       // calm-air single-leaf accumulator (very slow)
  private gustCooldown = 0;  // seconds before another gust flurry may fire, so ONE gust = ONE event (not every frame)
  private windX = 1; private windZ = 0; // latest wind heading (from update), so burst() can bias its scatter cone downwind

  constructor(count = 120) {
    const geo = new THREE.PlaneGeometry(0.24, 0.14);
    // LIT (Lambert): the leaves take the scene's sun + ambient, so they darken at night and warm at dusk instead
    // of glowing a fixed green. Per-instance colour is set on spawn to the tree the leaf fell from (so autumn/tinted
    // canopies shed matching leaves). transparent + fog so distant ones melt away.
    const mat = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide, transparent: true, opacity: 0.96, depthWrite: false, fog: true });
    this.mesh = new THREE.InstancedMesh(geo, mat, count);
    this.mesh.frustumCulled = false; // leaves live around the camera; the pool is tiny
    this.mesh.count = count;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < count; i++) {
      this.leaves.push({ x: 0, y: -9999, z: 0, vx: 0, vy: 0, vz: 0, gx: 0, gz: 0, spin: 0, ang: 0, life: 0, size: 1, alive: false });
      this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0));
      this.mesh.setColorAt(i, this.col.setRGB(0.4, 0.55, 0.22)); // seed the instanceColor buffer
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  // `leafyTrees()` returns up to a handful of nearby LEAFY canopies (no palms, no bare/dead trees), each with the
  // tree's foliage COLOUR. Behaviour: leaves are RARE in calm air (the odd single one every several seconds), but
  // when a GUST rolls through a few trees around you each shed a little flurry -- so it reads as the wind, never a
  // constant one-line stream.
  update(dt: number, time: number, wind: WindField, camX: number, camZ: number,
         groundAt: (x: number, z: number) => number, leafyTrees: () => Array<{ x: number; y: number; z: number; color: number }>): void {
    this.windX = wind.dirX; this.windZ = wind.dirZ; // remember for burst()'s downwind scatter cone
    this.gustCooldown -= dt;
    if (wind.gusting && this.gustCooldown <= 0) {
      const trees = leafyTrees();
      if (trees.length) {
        this.gustCooldown = 2.5 + Math.random() * 4;                 // don't re-fire for a few seconds
        const n = Math.min(trees.length, 2 + (Math.random() * 3 | 0)); // a few of the nearby trees, not all
        for (let k = 0; k < n; k++) { const t = trees[(Math.random() * trees.length) | 0]; this.burst(t.x, t.y, t.z, t.color, 2 + (Math.random() * 2 | 0)); }
      }
    }
    this.trickle += dt;
    if (this.trickle > 4 + Math.random() * 6) { // a lone stray leaf now and then even in calm air
      this.trickle = 0;
      const trees = leafyTrees();
      if (trees.length) { const t = trees[(Math.random() * trees.length) | 0]; this.spawn(t.x, t.y, t.z, t.color); }
    }
    let dirty = false;
    for (let i = 0; i < this.leaves.length; i++) {
      const l = this.leaves[i];
      if (!l.alive) continue;
      dirty = true;
      l.life -= dt;
      // WIDE wind-scatter: each leaf carries a persistent outward GLIDE (gx/gz, set downwind-biased on the burst)
      // that slowly decays, so it sails 5..30m from the tree and lands spread across a big random arc -- plus the
      // ambient drift + a flutter sway. A slow fall gives it the airtime to travel. (No more straight-down line.)
      l.gx *= 1 - Math.min(1, dt * 0.4); l.gz *= 1 - Math.min(1, dt * 0.4); // glide bleeds off over ~2.5s
      const drift = 0.5 + wind.gust * 1.1;
      l.vx += (wind.dirX * drift + l.gx + Math.sin(time * 2.5 + l.ang) * 0.5 - l.vx) * Math.min(1, dt * 2);
      l.vz += (wind.dirZ * drift + l.gz + Math.cos(time * 2.1 + l.ang) * 0.5 - l.vz) * Math.min(1, dt * 2);
      l.vy += -0.85 * dt;
      l.vy = Math.max(l.vy, -1.05);
      l.x += l.vx * dt; l.y += l.vy * dt; l.z += l.vz * dt;
      l.ang += l.spin * dt;
      const g = groundAt(l.x, l.z);
      if (l.y <= g + 0.05 || l.life <= 0 || Math.hypot(l.x - camX, l.z - camZ) > 95) { l.alive = false; this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0)); continue; }
      TMP_Q.setFromAxisAngle(LEAF_AXIS, l.ang); // tumble on a fixed tilted axis (no per-leaf allocation)
      this.mesh.setMatrixAt(i, TMP_M.compose(TMP_S.set(l.x, l.y, l.z), TMP_Q, TMP_S2.set(l.size, l.size, l.size)));
    }
    if (dirty) this.mesh.instanceMatrix.needsUpdate = true;
  }

  private spawn(x: number, y: number, z: number, color: number): void {
    const i = this.leaves.findIndex((n) => !n.alive);
    if (i < 0) return;
    const l = this.leaves[i];
    // A lone calm-air leaf: a small downwind-biased drift so even the stray one wanders a few metres, not straight down.
    const ang = Math.atan2(this.windZ, this.windX) + (Math.random() - 0.5) * 3.6;
    const spd = 1.5 + Math.random() * 3.5;
    l.x = x + (Math.random() - 0.5) * 3; l.y = y + Math.random() * 2; l.z = z + (Math.random() - 0.5) * 3;
    l.gx = Math.cos(ang) * spd; l.gz = Math.sin(ang) * spd;
    l.vx = l.gx * 0.5; l.vy = -0.2; l.vz = l.gz * 0.5;
    l.spin = (Math.random() - 0.5) * 4; l.ang = Math.random() * 6.28;
    l.size = 0.7 + Math.random() * 0.7; l.life = 9 + Math.random() * 5; l.alive = true;
    this.setLeafColor(i, color);
  }

  // Leaves knocked loose (a gust flurry, or a chop). Each is FLUNG in a WIDE random arc biased DOWNWIND, at a
  // random outward speed, so the flurry scatters across a big cone (roughly 5..30m from the trunk) and settles
  // very randomly -- never the tidy straight-down line. The wind heading is the one the update loop last saw.
  burst(x: number, y: number, z: number, color: number, n = 5): void {
    const wa = Math.atan2(this.windZ, this.windX);
    for (let k = 0; k < n; k++) {
      const i = this.leaves.findIndex((m) => !m.alive);
      if (i < 0) return;
      const l = this.leaves[i];
      const ang = wa + (Math.random() - 0.5) * 3.4;        // ~+/-97 deg cone around the wind: downwind-biased but very random
      const spd = 2.5 + Math.random() * 7.5;               // 2.5..10 m/s fling: some leaves land near, some sail ~30m out
      l.x = x + (Math.random() - 0.5) * 3.5; l.y = y + (Math.random() - 0.5) * 2; l.z = z + (Math.random() - 0.5) * 3.5;
      l.gx = Math.cos(ang) * spd; l.gz = Math.sin(ang) * spd;
      l.vx = l.gx * 0.7; l.vy = 0.2 + Math.random() * 0.7; l.vz = l.gz * 0.7;
      l.spin = (Math.random() - 0.5) * 6; l.ang = Math.random() * 6.28;
      l.size = 0.7 + Math.random() * 0.7; l.life = 8 + Math.random() * 5; l.alive = true;
      this.setLeafColor(i, color);
    }
  }

  private setLeafColor(i: number, color: number): void {
    const v = 0.55 + Math.random() * 0.25; // darker: the canopy multiplies a dark leaf texture, these are solid-lit // shade variation AT or below the canopy colour (never brighter than the tree)
    this.mesh.setColorAt(i, this.col.setRGB(((color >> 16) & 255) / 255 * v, ((color >> 8) & 255) / 255 * v, (color & 255) / 255 * v));
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

// ---- Chop debris ------------------------------------------------------------------------------------------
// Sticks/limbs knocked off a tree when you chop it: they arc out, settle on the ground, lie there, then fade +
// sink starting ~50s later so the forest floor briefly remembers the cut without piling up forever. Instanced +
// pooled, so it is one draw call and no allocation.
interface Stick { x: number; y: number; z: number; vx: number; vy: number; vz: number; ang: number; spin: number; tilt: number; age: number; grounded: boolean; alive: boolean; }
const FADE_START = 50, FADE_LEN = 10; // seconds: lie for 50s, then fade over 10s

export class ChopDebris {
  readonly mesh: THREE.InstancedMesh;
  private readonly sticks: Stick[] = [];

  constructor(count = 60) {
    const geo = new THREE.BoxGeometry(0.5, 0.06, 0.06); // a short bark stick
    const mat = new THREE.MeshLambertMaterial({ color: 0x6b4a2b, transparent: true, opacity: 1, fog: true });
    this.mesh = new THREE.InstancedMesh(geo, mat, count);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.count = count;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < count; i++) { this.sticks.push({ x: 0, y: -9999, z: 0, vx: 0, vy: 0, vz: 0, ang: 0, spin: 0, tilt: 0, age: 0, grounded: false, alive: false }); this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0)); }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  hit(x: number, y: number, z: number, n = 2): void {
    for (let k = 0; k < n; k++) {
      const s = this.sticks.find((m) => !m.alive);
      if (!s) return;
      s.x = x + (Math.random() - 0.5); s.y = y + Math.random() * 1.2; s.z = z + (Math.random() - 0.5);
      s.vx = (Math.random() - 0.5) * 3; s.vy = 1 + Math.random() * 1.5; s.vz = (Math.random() - 0.5) * 3;
      s.ang = Math.random() * 6.28; s.spin = (Math.random() - 0.5) * 6; s.tilt = Math.random() * 1.5;
      s.age = 0; s.grounded = false; s.alive = true;
    }
  }

  update(dt: number, groundAt: (x: number, z: number) => number): void {
    let dirty = false;
    for (let i = 0; i < this.sticks.length; i++) {
      const s = this.sticks[i];
      if (!s.alive) continue;
      dirty = true;
      s.age += dt;
      if (!s.grounded) {
        s.vy -= 9 * dt; s.x += s.vx * dt; s.y += s.vy * dt; s.z += s.vz * dt; s.ang += s.spin * dt;
        const g = groundAt(s.x, s.z);
        if (s.y <= g + 0.04) { s.y = g + 0.04; s.grounded = true; s.tilt = 1.55; } // land flat
      }
      if (s.age >= FADE_START + FADE_LEN) { s.alive = false; this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0)); continue; }
      TMP_Q.setFromAxisAngle(UP, s.ang).multiply(TMP_Q2.setFromAxisAngle(FWD_X, s.tilt));
      this.mesh.setMatrixAt(i, TMP_M.compose(TMP_S.set(s.x, s.y, s.z), TMP_Q, ONE));
    }
    if (dirty) this.mesh.instanceMatrix.needsUpdate = true;
    // Fade the whole pool out together once the oldest sticks pass FADE_START (cheap: one material opacity).
    const oldest = this.sticks.reduce((m, s) => (s.alive ? Math.max(m, s.age) : m), 0);
    (this.mesh.material as THREE.MeshLambertMaterial).opacity = oldest > FADE_START ? Math.max(0.15, 1 - (oldest - FADE_START) / FADE_LEN) : 1;
  }
}

// ---- Rock chips -------------------------------------------------------------------------------------------
// Little pebbles that spray out of an ORE/STONE node when you mine it -- the stone analogue of the chop debris.
// Each pops out on an arc, bounces to a settle, lies briefly, then SHRINKS away (per-instance scale, so chips
// vanish individually with no shared-opacity flicker). Per-instance COLOUR so a chip matches the node it flew
// off (grey stone / brown ore / yellow sulfur). Pooled + instanced -> one draw call, no allocation.
interface Chip { x: number; y: number; z: number; vx: number; vy: number; vz: number; ang: number; spin: number; age: number; life: number; grounded: boolean; size: number; alive: boolean; }

export class RockChips {
  readonly mesh: THREE.InstancedMesh;
  private readonly chips: Chip[] = [];
  private readonly col = new THREE.Color();

  constructor(count = 100) {
    const geo = new THREE.IcosahedronGeometry(0.06, 0); // a tiny angular pebble
    const mat = new THREE.MeshLambertMaterial({ flatShading: true, fog: true });
    this.mesh = new THREE.InstancedMesh(geo, mat, count);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.count = count;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < count; i++) {
      this.chips.push({ x: 0, y: -9999, z: 0, vx: 0, vy: 0, vz: 0, ang: 0, spin: 0, age: 0, life: 0, grounded: false, size: 1, alive: false });
      this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0));
      this.mesh.setColorAt(i, this.col.setRGB(0.6, 0.6, 0.6)); // seed the instanceColor buffer so setColorAt works later
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  // Spray `n` chips out of a mining hit at (x,y,z), tinted around `color` (0xRRGGBB, the node's rock colour).
  burst(x: number, y: number, z: number, color: number, n = 4): void {
    for (let k = 0; k < n; k++) {
      const c = this.chips.find((m) => !m.alive);
      if (!c) return;
      c.x = x + (Math.random() - 0.5) * 0.4; c.y = y + Math.random() * 0.3; c.z = z + (Math.random() - 0.5) * 0.4;
      c.vx = (Math.random() - 0.5) * 3.5; c.vy = 1.4 + Math.random() * 2.2; c.vz = (Math.random() - 0.5) * 3.5;
      c.ang = Math.random() * 6.28; c.spin = (Math.random() - 0.5) * 10;
      c.age = 0; c.life = 12 + Math.random() * 10; c.grounded = false; c.size = 0.6 + Math.random() * 0.9; c.alive = true;
      const v = 0.85 + Math.random() * 0.3; // per-chip shade variation around the node colour
      this.mesh.setColorAt(this.chips.indexOf(c), this.col.setRGB(((color >> 16) & 255) / 255 * v, ((color >> 8) & 255) / 255 * v, (color & 255) / 255 * v));
    }
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  update(dt: number, groundAt: (x: number, z: number) => number): void {
    let dirty = false;
    for (let i = 0; i < this.chips.length; i++) {
      const c = this.chips[i];
      if (!c.alive) continue;
      dirty = true;
      c.age += dt;
      if (!c.grounded) {
        c.vy -= 11 * dt; c.x += c.vx * dt; c.y += c.vy * dt; c.z += c.vz * dt; c.ang += c.spin * dt;
        const g = groundAt(c.x, c.z);
        if (c.y <= g + 0.03) { c.y = g + 0.03; c.grounded = true; c.vx *= 0.3; c.vz *= 0.3; } // land + kill horizontal speed
      }
      if (c.age >= c.life) { c.alive = false; this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0)); continue; }
      const s = c.size * (c.age > c.life - 2 ? Math.max(0, (c.life - c.age) / 2) : 1); // shrink away over the last 2s
      TMP_Q.setFromAxisAngle(LEAF_AXIS, c.ang);
      this.mesh.setMatrixAt(i, TMP_M.compose(TMP_S.set(c.x, c.y, c.z), TMP_Q, TMP_S2.set(s, s, s)));
    }
    if (dirty) this.mesh.instanceMatrix.needsUpdate = true;
  }
}

// ---- Birds ------------------------------------------------------------------------------------------------
// A RARE ambient touch, NOT a constant flock. Every few minutes a small flock crosses the sky in a loose V,
// passing off to one SIDE (never orbiting overhead), high and fog-melted, then it's gone and the sky is empty
// again. Daytime only -- birds roost at night. Pooled instances, one draw call, no per-frame allocation.
interface Bird { back: number; side: number; phase: number; flapRate: number; } // formation offset + flap params

const BIRD_SCALE = 1.3; // wingspan on screen; distant so a touch oversized to stay readable

export class Birds {
  readonly mesh: THREE.InstancedMesh;
  private readonly pool: Bird[] = [];
  private active = 0;                          // birds in the current pass (0 = empty sky)
  private timer = 25 + Math.random() * 45;     // seconds to the FIRST flock (start with an empty sky)
  private fx = 0; private fz = 0; private fy = 0; // flock-lead position
  private hx = 0; private hz = 1;              // heading (unit)
  private speed = 0; private flown = 0; private course = 0;

  constructor(private readonly max = 7) {
    // A dark swept "gull" silhouette, wings raised into a dihedral so a Y-squash reads as a wing-flap. Unlit,
    // no shadow, fog so distant ones melt into the sky.
    const geo = makeBirdGeo();
    const mat = new THREE.MeshBasicMaterial({ color: 0x2b2f33, side: THREE.DoubleSide, fog: true });
    this.mesh = new THREE.InstancedMesh(geo, mat, max);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false; this.mesh.receiveShadow = false;
    this.mesh.count = max;
    for (let i = 0; i < max; i++) { this.pool.push({ back: 0, side: 0, phase: 0, flapRate: 6 }); this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0)); }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  update(dt: number, time: number, camX: number, camZ: number, dayAmount = 1): void {
    if (this.active === 0) {
      // Empty sky: count down to the next flock, and only launch one in daylight.
      this.timer -= dt;
      if (this.timer <= 0) {
        if (dayAmount > 0.4) this.launch(camX, camZ);
        else this.timer = 10 + Math.random() * 15; // still night; check back shortly
      }
      return;
    }
    // Advance the flock in a straight glide with a gentle bob; flap the wings.
    this.flown += this.speed * dt;
    this.fx += this.hx * this.speed * dt;
    this.fz += this.hz * this.speed * dt;
    const px = -this.hz, pz = this.hx;            // perpendicular, for the sideways fan
    TMP_Q.setFromAxisAngle(UP, Math.atan2(this.hx, this.hz)); // whole flock faces the heading
    for (let i = 0; i < this.active; i++) {
      const b = this.pool[i];
      const x = this.fx - this.hx * b.back + px * b.side;
      const z = this.fz - this.hz * b.back + pz * b.side;
      const y = this.fy + Math.sin(time * 0.5 + b.phase) * 1.6;                       // slow bob
      const flap = 0.4 + 0.7 * (0.5 + 0.5 * Math.sin(time * b.flapRate + b.phase));   // wing dihedral flap
      this.mesh.setMatrixAt(i, TMP_M.compose(TMP_S.set(x, y, z), TMP_Q, TMP_S2.set(BIRD_SCALE, BIRD_SCALE * flap, BIRD_SCALE)));
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.flown >= this.course) this.land(); // flown its full course -> gone, sky empty again
  }

  private launch(camX: number, camZ: number): void {
    const size = 3 + Math.floor(Math.random() * 4); // 3..6 birds
    this.active = size;
    const dir = Math.random() * Math.PI * 2;
    this.hx = Math.cos(dir); this.hz = Math.sin(dir);
    const px = -this.hz, pz = this.hx;
    const side = (Math.random() < 0.5 ? -1 : 1) * (130 + Math.random() * 320); // 130..450m off to one side, never overhead
    this.course = 850 + Math.random() * 550;   // 850..1400m pass
    this.fy = 55 + Math.random() * 65;         // 55..120m up
    this.speed = 15 + Math.random() * 11;      // 15..26 m/s glide
    this.flown = 0;
    // Start half a course behind the crossing point (fixed at launch, NOT glued to the camera), so it flies in,
    // across, and out; if the player wanders off it just leaves.
    this.fx = camX + px * side - this.hx * (this.course * 0.5);
    this.fz = camZ + pz * side - this.hz * (this.course * 0.5);
    // Loose V: lead in front, the rest staggered behind and fanned to alternating sides.
    for (let i = 0; i < size; i++) {
      const rank = Math.ceil(i / 2), b = this.pool[i];
      b.back = rank * (6.5 + Math.random() * 3);
      b.side = i === 0 ? 0 : (i % 2 === 0 ? 1 : -1) * rank * (5.5 + Math.random() * 3);
      b.phase = Math.random() * 6.28;
      b.flapRate = 5 + Math.random() * 3;
    }
    for (let i = size; i < this.max; i++) this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0)); // hide the rest of the pool
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  private land(): void {
    this.active = 0;
    this.timer = 95 + Math.random() * 160; // rare: ~1.5..4 min of empty sky before the next flock
    for (let i = 0; i < this.max; i++) this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0));
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  // A gunshot near a passing flock makes it climb + bolt (only if one is actually in the sky).
  scatter(x: number, z: number): void {
    if (this.active === 0) return;
    if (Math.hypot(this.fx - x, this.fz - z) < 420) { this.speed = Math.min(this.speed * 1.7, 55); this.fy += 12; }
  }
}

// ---- Lightning scorch -------------------------------------------------------------------------------------
// PERMANENT charred marks where lightning struck the ground. A pool of dark decal discs laid flat on the
// terrain (ring-buffered: once full, the oldest is overwritten). Paired with grass.addBurn() so grass also
// stops growing on the mark -- the map keeps a record of every strike you've been near.
export class ScorchField {
  readonly mesh: THREE.InstancedMesh;
  private next = 0;

  constructor(private readonly max = 120) {
    const geo = new THREE.CircleGeometry(1, 18);
    geo.rotateX(-Math.PI / 2); // lay flat on the ground (XZ plane)
    const mat = new THREE.MeshBasicMaterial({ color: 0x171310, transparent: true, opacity: 0.88, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, fog: true });
    this.mesh = new THREE.InstancedMesh(geo, mat, max);
    this.mesh.frustumCulled = false;
    this.mesh.receiveShadow = false; this.mesh.castShadow = false;
    this.mesh.count = max;
    for (let i = 0; i < max; i++) this.mesh.setMatrixAt(i, TMP_M.makeScale(0, 0, 0));
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  add(x: number, y: number, z: number, r: number): void {
    const i = this.next; this.next = (this.next + 1) % this.max;
    TMP_Q.setFromAxisAngle(UP, Math.random() * 6.28); // random spin so no two scorch discs look cloned
    this.mesh.setMatrixAt(i, TMP_M.compose(TMP_S.set(x, y + 0.04, z), TMP_Q, TMP_S2.set(r, 1, r)));
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

function makeBirdGeo(): THREE.BufferGeometry {
  // A swept gull silhouette pointing +Z (forward): body at the nose, each wing sweeps back (-Z) and out (+/-X)
  // to a RAISED tip (+Y). The raised tips give a dihedral, so the update's Y-squash reads as a wing-flap.
  const s = 1.0;
  const v = new Float32Array([
    0, 0, s * 0.35, -s * 0.5, s * 0.16, -s * 0.25, -s * 1.5, s * 0.5, -s * 0.35, // left wing: nose -> elbow -> raised swept tip
    0, 0, s * 0.35, s * 0.5, s * 0.16, -s * 0.25, s * 1.5, s * 0.5, -s * 0.35,   // right wing
  ]);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(v, 3));
  g.computeVertexNormals();
  return g;
}
